import type { TaxonomyNode, TaxonomyUrn } from "@shared/types/taxonomy.ts";

/** A node of the subject skeleton, linked to its parent, its domain and its children. */
export class TaxonomyNodeView {
  /** Linked by TaxonomyView once every node exists. */
  parent: TaxonomyNodeView | null = null;
  readonly children: TaxonomyNodeView[] = [];

  constructor(readonly node: TaxonomyNode) {}

  get urn(): TaxonomyUrn {
    return this.node.urn;
  }

  get enName(): string {
    return this.node.enName;
  }

  get isField(): boolean {
    return this.node.level === "field";
  }

  /** The domain the node belongs to (itself for a domain), or null for an orphan field. */
  get domain(): TaxonomyNodeView | null {
    return this.node.level === "domain" ? this : this.parent;
  }

  /** The domain's short key, the last segment of its URN ("health" for urn:rc:taxonomy:health). */
  get domainKey(): string | null {
    return this.domain?.urn.split(":").pop() ?? null;
  }
}

/** The subject skeleton the API returns, with its nodes linked and indexed once. */
export class TaxonomyView {
  readonly domains: TaxonomyNodeView[];
  readonly fields: TaxonomyNodeView[];
  private readonly byUrn: Map<TaxonomyUrn, TaxonomyNodeView>;

  constructor(nodes: readonly TaxonomyNode[]) {
    const views = nodes.map((node) => new TaxonomyNodeView(node));
    this.byUrn = new Map(views.map((view) => [view.urn, view]));
    for (const view of views) {
      const parent = view.node.parentUrn === null ? null : this.byUrn.get(view.node.parentUrn);
      if (parent) {
        view.parent = parent;
        parent.children.push(view);
      }
    }
    this.domains = views.filter((view) => !view.isField);
    this.fields = views.filter((view) => view.isField);
  }

  /** True until the skeleton has loaded (and for a visitor who is not signed in). */
  get isEmpty(): boolean {
    return this.byUrn.size === 0;
  }

  /** The node with this URN, or null when the skeleton has none. */
  find(urn: TaxonomyUrn): TaxonomyNodeView | null {
    return this.byUrn.get(urn) ?? null;
  }

  /** The node with this URN when it is a field, or null. */
  findField(urn: TaxonomyUrn): TaxonomyNodeView | null {
    const view = this.find(urn);
    return view?.isField ? view : null;
  }
}
